/**
 * Application access, distinct from the JWT role.
 * Super admin is the JWT `admin` claim. The other three share JWT `user`
 * and are distinguished by GET /v1/me/access.
 */
export const ACCESS_ROLES = ['super_admin', 'org_admin', 'team_manager', 'member'] as const;

export type AccessRole = (typeof ACCESS_ROLES)[number];

export const ME_ACCESS_ENDPOINT = '/v1/me/access';

/** Code of the 403 the server sends when the caller's role no longer fits the route. */
export const ROLE_NOT_ALLOWED_CODE = 'role_not_allowed';

/** Code of the 403 refusing a new session while the organization is suspended. */
export const ORG_SUSPENDED_CODE = 'org_suspended';

export const ORGANIZATION_STATUS = { ACTIVE: 'active', SUSPENDED: 'suspended' } as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUS)[keyof typeof ORGANIZATION_STATUS];

export interface MeOrganization {
  uuid: string;
  code: string;
  name: string;
  status: OrganizationStatus;
}

export interface MeTeam {
  uuid: string;
  name: string;
}

export interface MeAccess {
  role: AccessRole;
  organization: MeOrganization | null;
  team: MeTeam | null;
}

export function isAccessRole(value: unknown): value is AccessRole {
  return typeof value === 'string' && (ACCESS_ROLES as readonly string[]).includes(value);
}

export function meAccessQueryKey(userPublicId: string | null) {
  return ['core', 'me', 'access', userPublicId] as const;
}
